import React from 'react';
import { ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ComposedChart, Bar, Line, AreaChart, Area } from 'recharts';
import { useCrossAnalysisStats } from '../../hooks/useCrossAnalysisStats';
import LoadingSpinner from '../common/LoadingSpinner';
import StatsCard from '../common/StatsCard';
import { formatCurrency } from '../../utils/formatters';

interface Props {
  dateRange: { start_date?: string; end_date?: string };
}

export default function CrossAnalysisTab({ dateRange }: Props) {
  const { data: stats, isLoading, error } = useCrossAnalysisStats(dateRange);

  if (isLoading) return <LoadingSpinner />;
  if (error) return <div className="text-red-500">Erreur lors du chargement des données</div>;
  if (!stats) return null;

  // Données pour l'analyse saisonnière
  const seasonalData = stats.seasonal_trends.map(trend => ({
    month: new Date(trend.month).toLocaleDateString('fr-FR', { month: 'short' }),
    patients: trend.patients,
    revenue: trend.revenue,
    revenue_per_patient: trend.patients > 0 ? trend.revenue / trend.patients : 0
  }));

  // Top productivité médecins
  const topDoctors = stats.productivity_by_doctor
    .sort((a, b) => b.revenue_per_day - a.revenue_per_day)
    .slice(0, 10);

  return (
    <div className="space-y-6">
      {/* KPIs Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <StatsCard
          title="Revenus par Patient"
          value={formatCurrency(stats.revenue_per_patient)}
          icon="👤"
          color="blue"
        />
        <StatsCard
          title="Taux d'Occupation"
          value={`${stats.occupancy_rate.toFixed(1)}%`}
          icon="🏥"
          color={stats.occupancy_rate >= 80 ? "green" : stats.occupancy_rate >= 60 ? "yellow" : "red"}
        />
        <StatsCard
          title="Productivité Moyenne"
          value={`${stats.productivity_by_doctor.length > 0 ? (stats.productivity_by_doctor.reduce((sum, d) => sum + d.patients_per_day, 0) / stats.productivity_by_doctor.length).toFixed(1) : 0} pat/jour`}
          icon="⚡"
          color="purple"
        />
        <StatsCard
          title="Marge Assurance Moy"
          value={`${stats.insurance_profitability.length > 0 ? (stats.insurance_profitability.reduce((sum, i) => sum + i.profit_margin, 0) / stats.insurance_profitability.length).toFixed(1) : 0}%`}
          icon="💰"
          color="green"
        />
      </div>

      {/* Analyse saisonnière */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Analyse Saisonnière - Patients vs Revenus</h3>
        <ComposedChart width={1000} height={400} data={seasonalData}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="month" />
          <YAxis yAxisId="left" />
          <YAxis yAxisId="right" orientation="right" />
          <Tooltip formatter={(value, name) => {
            if (name === 'patients') return [value, 'Patients'];
            if (name === 'revenue') return [formatCurrency(Number(value)), 'Revenus'];
            return [formatCurrency(Number(value)), 'Rev/Patient'];
          }} />
          <Legend />
          <Bar yAxisId="left" dataKey="patients" fill="#8884d8" name="Patients" />
          <Line yAxisId="right" type="monotone" dataKey="revenue" stroke="#ff7300" strokeWidth={2} name="Revenus" />
          <Line yAxisId="right" type="monotone" dataKey="revenue_per_patient" stroke="#00ff00" strokeWidth={2} name="Rev/Patient" />
        </ComposedChart>
      </div>

      {/* Productivité par médecin */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Productivité par Médecin</h3>
          <ScatterChart width={500} height={350} data={topDoctors}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="patients_per_day" name="Patients/jour" />
            <YAxis dataKey="revenue_per_day" name="Revenus/jour" />
            <Tooltip cursor={{ strokeDasharray: '3 3' }} 
              formatter={(value, name) => [
                name === 'patients_per_day' ? `${value} patients/jour` : `${formatCurrency(Number(value))}/jour`,
                name === 'patients_per_day' ? 'Patients' : 'Revenus'
              ]} />
            <Scatter dataKey="revenue_per_day" fill="#8884d8" />
          </ScatterChart>
        </div>

        {/* Profitabilité par assurance */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Profitabilité par Assurance</h3>
          <AreaChart width={500} height={350} data={stats.insurance_profitability}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="insurance_name" angle={-45} textAnchor="end" height={100} />
            <YAxis />
            <Tooltip formatter={(value, name) => [
              name === 'profit_margin' ? `${value}%` : formatCurrency(Number(value)),
              name === 'profit_margin' ? 'Marge' : 'Valeur Moy'
            ]} />
            <Area type="monotone" dataKey="avg_treatment_value" stackId="1" stroke="#8884d8" fill="#8884d8" />
            <Area type="monotone" dataKey="profit_margin" stackId="2" stroke="#82ca9d" fill="#82ca9d" />
          </AreaChart>
        </div>
      </div>

      {/* Tableau de bord détaillé */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top médecins */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Top Médecins par Performance</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Médecin</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Pat/Jour</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rev/Jour</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {topDoctors.slice(0, 8).map((doctor, index: number) => (
                  <tr key={index}>
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">{doctor.doctor_name}</td>
                    <td className="px-6 py-4 text-sm text-gray-500">{doctor.patients_per_day.toFixed(1)}</td>
                    <td className="px-6 py-4 text-sm text-gray-500">{formatCurrency(doctor.revenue_per_day)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Analyse assurances */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Performance par Assurance</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Assurance</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Val. Moy</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Délai</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Marge</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {stats.insurance_profitability.map((insurance, index: number) => (
                  <tr key={index}>
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">{insurance.insurance_name}</td>
                    <td className="px-6 py-4 text-sm text-gray-500">{formatCurrency(insurance.avg_treatment_value)}</td>
                    <td className="px-6 py-4 text-sm text-gray-500">{insurance.payment_delay} jours</td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      <span className={`px-2 py-1 text-xs rounded ${
                        insurance.profit_margin >= 20 ? 'bg-green-100 text-green-800' :
                        insurance.profit_margin >= 10 ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {insurance.profit_margin.toFixed(1)}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}